import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { setupApiClient } from "@semo/api-contracts";
import App from "./App";
import { queryClient } from "./lib/queryClient";
import "@semo/shared-styles";
import "@semo/icons/tokens.css";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  // Replay 는 첫 화면에 필요 없다. 정적으로 넣으면 잔존 122 KB 가 초기 청크에 실린다 —
  // 세션 녹화는 페이지가 뜬 뒤에 붙어도 늦지 않다.
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Ignore specific string-based errors
  ignoreErrors: [
    "Failed to execute 'removeChild' on 'Node'",
    "Acquiring an exclusive Navigator LockManager lock",
    "Network connection failed",
    "Request timeout",
    "Network Error",
  ],
  // Filter out noisy network errors
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Ignore Axios/Network errors with specific client error statuses
    if (error && typeof error === "object" && "response" in error) {
      const response = error.response;
      if (response && typeof response === "object" && "status" in response) {
        const status = response.status;
        if (
          typeof status === "number" &&
          [400, 401, 403, 404, 409, 429].includes(status)
        ) {
          return null;
        }
      }
    }

    return event;
  },
});

// 첫 페인트 뒤에 Replay 를 붙인다(실패해도 앱은 그대로 돈다).
if (!import.meta.env.DEV) {
  void Sentry.lazyLoadIntegration("replayIntegration")
    .then((replay) => Sentry.addIntegration(replay()))
    .catch(() => undefined);
}

// Initialize API client with environment configuration
// 파트너 API 키(X-API-Key)는 여기 싣지 않는다 — 번들에 들어간 값은 누구나 읽는다.
// 판매자 센터가 부르는 경로는 전부 공급사 세션(Bearer)으로 인증한다.
setupApiClient({
  baseURL: import.meta.env.VITE_API_URL ?? "/",
  withCredentials: true,
  headers: {
    "x-client-type": "vendor",
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
